import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
// import axios from 'axios'; // Removed direct axios usage
import { useAuth } from '../contexts/AuthContext';
import { AdminSubscriptionModal } from '../components/AdminSubscriptionModal';
import { AdminUserTable } from '../components/AdminUserTable';
import { AdminUserDialog } from '../components/AdminUserDialog';
import { NotificationModal } from '../components/NotificationModal';
import './Dashboard.css';
import './AdminPage.css';
import { 
  useScrapers, 
  useUsers, 
  useSystemHealth, 
  useToggleScraper, 
  useUpdateScraperNote, 
  useAcknowledgeError,
  useUpdateUserTier,
  useUpdateUserTelegram,
  useUpdateUserExpiry,
  useUpdateUserEmail,
  useResetUserPassword,
  useDeleteUser,
  useSendPushTest,
  useSendTestEmail,
  useSendTestTelegram,
  useRebuildSystem,
  useSendGlobalNotification,
  useSetMaintenanceMode,
  useStaleScrapers,
  useDeleteStaleScrapers,
  useScraperSchedules,
  useToggleScraperSchedules,
  useTriggerScraperRun,
  adminKeys
} from '../hooks/queries/useAdmin';
import { ScraperState, UserData, StaleRecord, impersonateUser, resetEmailVerification } from '../api/admin';
import { isDevEnvironment } from '../components/DevIndicator';
import { ScraperScheduleBadge } from '../components/ScraperScheduleBadge';
import { ScheduleProfilesPanel } from '../components/ScheduleProfilesPanel';

type SortColumn = 'name' | 'lastChange' | 'results' | 'status';
type SortDirection = 'asc' | 'desc';

interface PasswordResetResult {
  userId: string;
  email?: string;
  newPassword?: string;
}

const AdminPage: React.FC = () => {
  useAuth();
  
  // React Query Hooks
  const { data: scrapers = [], isLoading: isLoadingScrapers, refetch: refetchScrapers } = useScrapers();
  const { data: users = [], isLoading: isLoadingUsers, refetch: refetchUsers } = useUsers();
  const [isRefreshingUsers, setIsRefreshingUsers] = useState(false);
  
  const [isRebuilding, setIsRebuilding] = useState<string | null>(null);
  
  // Use a query client to check current data for status-based polling
  const queryClient = useQueryClient();
  const { data: systemStatsData } = useSystemHealth(60000);
  const toggleScraperMutation = useToggleScraper();
  const updateNoteMutation = useUpdateScraperNote();
  const acknowledgeErrorMutation = useAcknowledgeError();
  const updateUserTierMutation = useUpdateUserTier();
  const updateUserTelegramMutation = useUpdateUserTelegram();
  const updateUserExpiryMutation = useUpdateUserExpiry();
  const updateUserEmailMutation = useUpdateUserEmail();
  const resetPasswordMutation = useResetUserPassword();
  const deleteUserMutation = useDeleteUser();
  const sendPushTestMutation = useSendPushTest();
  const sendTestEmailMutation = useSendTestEmail();
  const sendTestTelegramMutation = useSendTestTelegram();
  const rebuildSystemMutation = useRebuildSystem();
  const sendNotificationMutation = useSendGlobalNotification();
  const setMaintenanceModeMutation = useSetMaintenanceMode();
  const deleteStaleScrapersMutation = useDeleteStaleScrapers();
  const { data: scheduleData, isLoading: isLoadingSchedules } = useScraperSchedules();
  const toggleSchedulesMutation = useToggleScraperSchedules();
  const triggerScraperMutation = useTriggerScraperRun();
  const [scraperFilterInput, setScraperFilterInput] = useState('');
  const [scraperTableFilter, setScraperTableFilter] = useState('');
  const [testModeEnabled, setTestModeEnabled] = useState(false);

  // Maintenance message form state
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [maintenanceSeverity, setMaintenanceSeverity] = useState<'INFO' | 'WARNING' | 'MAINTENANCE' | 'OUTAGE'>('MAINTENANCE');
  const [maintenanceText, setMaintenanceText] = useState('');
  const [isTogglingMaintenance, setIsTogglingMaintenance] = useState(false);

  // Local State
  // const [scrapers, setScrapers] = useState<ScraperState[]>([]); // Replaced by hook
  // const [users, setUsers] = useState<UserData[]>([]); // Replaced by hook
  // const [isLoading, setIsLoading] = useState(true); // Replaced by hook statuses
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [resetResult, setResetResult] = useState<PasswordResetResult | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>('status');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [noteValue, setNoteValue] = useState('');
  const [selectedError, setSelectedError] = useState<ScraperState | null>(null);
  const [schedulePopover, setSchedulePopover] = useState<string | null>(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  // const [isMaintenance, setIsMaintenance] = useState(false); // Derived from systemStatsData
  // const [systemStats, setSystemStats] = useState<{ zipCount: number; totalSubscriptions: number } | null>(null); // Derived
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  // const [subscriptionsByUser, setSubscriptionsByUser] = useState<Record<string, any[]>>({}); // Removed
  const [selectedSubscription, setSelectedSubscription] = useState<any | null>(null);
  // const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState<Record<string, boolean>>({}); // Removed
  const [expandedScrapers, setExpandedScrapers] = useState<Set<string>>(new Set());
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [impersonationLink, setImpersonationLink] = useState<string | null>(null);
  const [notificationProgress, setNotificationProgress] = useState<'idle' | 'sending' | 'success'>('idle');
  const [showStalePanel, setShowStalePanel] = useState(false);
  const [selectedStaleKeys, setSelectedStaleKeys] = useState<Set<string>>(new Set());
  const [isDeletingStale, setIsDeletingStale] = useState(false);

  const { data: staleData, isLoading: isLoadingStale, refetch: refetchStale } = useStaleScrapers(showStalePanel);

  const isMaintenance = systemStatsData?.maintenanceMode || false;
  const systemStats = systemStatsData ? { 
    zipCount: systemStatsData.metadata.zipCount, 
    totalSubscriptions: systemStatsData.subscriptions.total 
  } : null;

  const isLoading = isLoadingScrapers || isLoadingUsers;


  // Helper function to remove "Scraper" suffix from search keys
  const formatScraperName = (searchKey: string): string => {
    let name = searchKey.replace(/Scraper/, '');
    if (name.includes('-Group-')) {
        const [vendor, rest] = name.split('-Group-');
        const districtIds = rest.split('-');
        
        // If more than 3 district IDs, truncate with ellipsis
        let displayIds: string;
        if (districtIds.length > 3) {
            displayIds = `${districtIds.slice(0, 2).join('-')}...+${districtIds.length - 2}`;
        } else {
            displayIds = rest;
        }
        
        return `${vendor} Group (${displayIds})`;
    }
    return name;
  };

  const toggleScraperGroup = (searchKey: string) => {
    const next = new Set(expandedScrapers);
    if (next.has(searchKey)) {
        next.delete(searchKey);
    } else {
        next.add(searchKey);
    }
    setExpandedScrapers(next);
  };

  // useEffect(() => {
  //   fetchData();
  // }, []);

  // Keep selectedUser in sync with latest query data (after mutations + refetch)
  useEffect(() => {
    if (selectedUser && users.length > 0) {
      const updated = users.find(u => u.userId === selectedUser.userId);
      if (updated && updated !== selectedUser) setSelectedUser(updated);
    }
  }, [users]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedError(null);
        setResetResult(null);
        setSchedulePopover(null);
      }
    };
    const handleClickOutside = () => setSchedulePopover(null);
    window.addEventListener('keydown', handleEscape);
    window.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // const fetchData = async () => { ... } // Removed


  const handleRebuild = async (type: 'metadata', force: boolean = false) => {
    const confirmMsg = 'SubscriptionsAggregate wirklich neu aufbauen?';
    
    if (!window.confirm(confirmMsg)) return;

    setIsRebuilding(type);
    try {
      await rebuildSystemMutation.mutateAsync({ type, force });
      console.debug(`Metadata rebuild triggered!`);
    } catch (err: any) {
      alert(`Fehler: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsRebuilding(null);
    }
  };

  const handleAcknowledgeError = async () => {
    if (!selectedError) return;

    setIsAcknowledging(true);
    try {
      await acknowledgeErrorMutation.mutateAsync(selectedError.searchKey);
      setSelectedError(null);
    } catch (err: any) {
      console.error('Failed to acknowledge error:', err);
      setError('Failed to acknowledge error');
    } finally {
      setIsAcknowledging(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const minDelay = new Promise(resolve => setTimeout(resolve, 500));
    try {
      await Promise.all([
        refetchScrapers(), 
        queryClient.invalidateQueries({ queryKey: adminKeys.systemHealth() }),
        minDelay
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const toggleScraper = async (searchKey: string, currentStatus: boolean | undefined) => {
    try {
      await toggleScraperMutation.mutateAsync({ searchKey, activate: !!currentStatus });
    } catch (err) {
      alert('Action failed');
    }
  };

  const updateScraperNote = async (searchKey: string, note: string) => {
    try {
        await updateNoteMutation.mutateAsync({ searchKey, note });
        setEditingNote(null);
    } catch (err) {
      alert('Failed to update note');
      console.error(err);
    }
  };

  const updateUser = async (userId: string, field: string, value: any) => {
    try {
        if (field === 'tier') {
            await updateUserTierMutation.mutateAsync({ userId, tier: value });
        } else if (field === 'telegramId') {
            await updateUserTelegramMutation.mutateAsync({ userId, telegramId: value });
        } else if (field === 'premiumExpiresAt') {
            await updateUserExpiryMutation.mutateAsync({ userId, expiry: value });
        } else if (field === 'email') {
            const normalizedEmail = (value === '' || value === null) ? null : value;
            await updateUserEmailMutation.mutateAsync({ userId, email: normalizedEmail });
        }
    } catch (err) {
        alert('Update failed');
    }
  };

  const handleResetPassword = async (user: UserData) => {
    if (!window.confirm(`Passwort für ${user.email} wirklich zurücksetzen?`)) return;
    
    setIsResetting(true);
    try {
        const newPassword = await resetPasswordMutation.mutateAsync({ userId: user.userId });
        setResetResult({ userId: user.userId, email: user.email || '', newPassword });
    } catch (err) {
        alert('Passwort-Reset fehlgeschlagen');
    } finally {
        setIsResetting(false);
    }
  };

  const handleDeleteUser = async (user: UserData) => {
    const label = user.email || user.telegramId || user.userId;
    if (!window.confirm(`User "${label}" wirklich löschen? Alle Suchen werden entfernt. Zahlungsdaten bleiben erhalten.`)) return;

    setIsDeleting(true);
    try {
        await deleteUserMutation.mutateAsync({ userId: user.userId });
    } catch (err: any) {
        alert(`Löschen fehlgeschlagen: ${err.response?.data?.error || err.message}`);
    } finally {
        setIsDeleting(false);
    }
  };

  const handleTestPush = async (userId: string) => {
    if(!window.confirm('Test-Push senden?')) return;
    
    try {
        await sendPushTestMutation.mutateAsync({ userId });
        alert('Test-Push erfolgreich gesendet!');
    } catch (err: any) {
        alert(`Fehler: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleTestEmail = async (userId: string) => {
    if(!window.confirm('Test-Email senden?')) return;
    try {
        await sendTestEmailMutation.mutateAsync({ userId });
        alert('Test-Email erfolgreich gesendet!');
    } catch (err: any) {
        alert(`Fehler: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleTestTelegram = async (userId: string) => {
    if(!window.confirm('Test-Telegram senden?')) return;
    try {
        await sendTestTelegramMutation.mutateAsync({ userId });
        alert('Test-Telegram erfolgreich gesendet!');
    } catch (err: any) {
        alert(`Fehler: ${err.response?.data?.error || err.message}`);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('In die Zwischenablage kopiert');
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column with ascending direction
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getHealthPriority = (s: ScraperState): number => {
    if (s.deactivated) return 0;
    if ((s.errorCounter || 0) >= 10) return 1;
    if ((s.errorCounter || 0) > 0) return 2;
    if (s.tokenStatus === 'fetching') return 3;
    if (s.lastError && (s.lastRecoverTimestamp || 0) > (s.lastErrorTimestamp || 0)) return 4;
    return 5; // healthy
  };

  const getSortedScrapers = (): ScraperState[] => {
    const sorted = [...scrapers].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'name':
          aValue = formatScraperName(a.searchKey).toLowerCase();
          bValue = formatScraperName(b.searchKey).toLowerCase();
          break;
        case 'lastChange':
          aValue = a.lastChange || 0;
          bValue = b.lastChange || 0;
          break;
        case 'results':
          aValue = (a as any).resultCount ?? 0;
          bValue = (b as any).resultCount ?? 0;
          break;
        case 'status':
          aValue = getHealthPriority(a);
          bValue = getHealthPriority(b);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;

      // Secondary sort by lastChange (most recent first) when status is the same
      if (sortColumn === 'status') {
        const aTime = a.lastChange || 0;
        const bTime = b.lastChange || 0;
        return bTime - aTime;
      }

      return 0;
    });

    return sorted;
  };




  const RefreshButton = ({ onClick, spinning }: { onClick: () => void; spinning: boolean }) => (
    <button 
      onClick={onClick} 
      className="icon-btn" 
      title="Aktualisieren"
      disabled={spinning}
      style={{ 
        border: 'none',
        background: 'transparent', 
        padding: 0,
        width: 'auto',
        height: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer'
      }}
    >
      <svg 
        width="16" 
        height="16" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2.5" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ 
          animation: spinning ? 'spin 1s linear infinite' : 'none',
          color: spinning ? 'rgb(var(--c-brand))' : 'rgb(var(--c-text-muted))'
        }}
      >
        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/>
      </svg>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );
  const NavItem: React.FC<{ target: string; label: string }> = ({ target, label }) => (
    <a 
      href={`#${target}`} 
      onClick={(e) => {
        e.preventDefault();
        document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }}
      style={{ 
        padding: '8px 16px', 
        color: 'rgb(var(--c-text-muted))', 
        fontSize: '14px', 
        fontWeight: 500, 
        textDecoration: 'none',
        borderRadius: '6px',
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
        e.currentTarget.style.color = 'var(--accent-blue)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.color = 'var(--text-muted)';
      }}
    >
      {label}
    </a>
  );

  return (
    <div className="dashboard">
      {/* DEV Environment Banner */}
      {isDevEnvironment() && (
        <div style={{
          background: 'rgb(var(--c-warning-surface))',
          color: 'rgb(var(--c-warning-text))',
          textAlign: 'center',
          padding: '6px 12px',
          fontWeight: 700,
          fontSize: '0.85rem',
          letterSpacing: '0.05em',
        }}>
          ⚠️ DEV ENVIRONMENT — dev.schneller-wohnen.net
        </div>
      )}

      {/* Sticky Sub-Navigation */}
      <div className="admin-sticky-nav">
        <div className="dashboard-container" style={{ padding: '0 24px', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <NavItem target="scrapers" label="Scrapers" />
          <NavItem target="system" label="System" />
          <NavItem target="users" label="Users" />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button 
              className="admin-btn-primary"
              onClick={() => {
                setNotificationProgress('idle');
                setShowNotificationModal(true);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
              </svg>
              Benachrichtigung
            </button>
            <RefreshButton onClick={handleRefresh} spinning={isRefreshing} />
          </div>
        </div>
      </div>

      <div className="dashboard-container">
        {error && <div className="alert alert-error">{error}</div>}
        
        {/* Maintenance Mode Banner */}
        {isMaintenance && (
          <div className="admin-alert-banner maintenance">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span style={{ fontWeight: 600 }}>System befindet sich im Wartungsmodus (Scraper pausiert)</span>
          </div>
        )}
        
        {isLoading && !isRefreshing ? (
            <div className="loading">Loading...</div>
        ) : (
            <>
                {/* Scrapers Section */}
                <div id="scrapers" className="section" style={{ marginBottom: '40px' }}>
                    <div className="section-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <h2>Scraper Status</h2>
                        </div>
                    </div>

                    {/* Schedule Control Card */}
                    <div className="admin-card" style={{ marginBottom: '20px', padding: '16px', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1rem' }}>Schedule Control</h3>
                                {isLoadingSchedules ? (
                                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Loading...</span>
                                ) : scheduleData?.schedules?.length ? (
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                                        {(() => {
                                            const groupOrder = ['Group2', 'Group3'];
                                            const getGroup = (name: string) => {
                                                for (const g of groupOrder) {
                                                    if (name?.includes(g)) return g;
                                                }
                                                return 'Group1';
                                            };
                                            const grouped = scheduleData.schedules.reduce((acc, s) => {
                                                const g = getGroup(s.name || '');
                                                (acc[g] = acc[g] || []).push(s);
                                                return acc;
                                            }, {} as Record<string, typeof scheduleData.schedules>);
                                            const allGroups = ['Group1', ...groupOrder].filter(g => grouped[g]?.length);
                                            const labels: Record<string, string> = { Group1: 'G1 – Fast/Init/APIs', Group2: 'G2 – Browser', Group3: 'G3 – Medium' };
                                            return allGroups.map(g => (
                                                <div key={g} style={{ marginBottom: '4px' }}>
                                                    <span style={{ fontWeight: 600, marginRight: '8px' }}>{labels[g] || g}:</span>
                                                    {grouped[g].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((s, i) => (
                                                        <span key={i} style={{ marginRight: '10px' }}>
                                                            <span style={{ 
                                                                display: 'inline-block', 
                                                                width: '8px', height: '8px', 
                                                                borderRadius: '50%', 
                                                                backgroundColor: s.state === 'ENABLED' ? 'rgb(var(--c-success-text))' : 'rgb(var(--c-danger-text))',
                                                                marginRight: '4px'
                                                            }} />
                                                            {(s.description || s.name?.replace(`-${scheduleData.environment}`, '') || '').replace(/^ListingsScraperScrapingSchedule/, '').replace(/Group\d/g, '')}
                                                        </span>
                                                    ))}
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                ) : (
                                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No schedules found</span>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                {scheduleData?.schedules?.length ? (
                                    <button
                                        className={`admin-btn-primary`}
                                        disabled={toggleSchedulesMutation.isPending}
                                        onClick={() => {
                                            const allEnabled = scheduleData.schedules.every(s => s.state === 'ENABLED');
                                            toggleSchedulesMutation.mutate(!allEnabled);
                                        }}
                                        style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                                    >
                                        {toggleSchedulesMutation.isPending ? '...' : 
                                            scheduleData.schedules.every(s => s.state === 'ENABLED') ? '⏸ Disable' : '▶ Enable'}
                                    </button>
                                ) : null}
                                <input
                                    type="text"
                                    placeholder="Filter scrapers (e.g. Vonovia,Immoscout)"
                                    value={scraperFilterInput}
                                    onChange={e => setScraperFilterInput(e.target.value)}
                                    style={{
                                        padding: '5px 10px',
                                        fontSize: '0.82rem',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '6px',
                                        width: '240px',
                                        background: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)',
                                    }}
                                    title="Comma-separated scraper names or instance keys (e.g. Vonovia, Immomio-vhw, Immowelt-Group-AD06DE65). Leave empty to run all. Click ▶ on a row to fill."
                                />
                                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.82rem', cursor: 'pointer', userSelect: 'none' }}
                                    title="Injects testmode=true into every scraper's searchParams — marks first 10 entries as new, sends notifications with [TEST] prefix.">
                                    <input
                                        type="checkbox"
                                        checked={testModeEnabled}
                                        onChange={e => setTestModeEnabled(e.target.checked)}
                                    />
                                    🧪 Test mode
                                </label>
                                <button
                                    className="admin-btn-primary"
                                    disabled={triggerScraperMutation.isPending}
                                    onClick={() => {
                                        const filter = scraperFilterInput
                                            .split(',')
                                            .map(s => s.trim())
                                            .filter(Boolean);
                                        triggerScraperMutation.mutate({
                                            scraperFilter: filter.length > 0 ? filter : undefined,
                                            testMode: testModeEnabled || undefined,
                                        });
                                    }}
                                    style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                                >
                                    {triggerScraperMutation.isPending ? '...' : '🚀 Run Now'}
                                </button>
                                {triggerScraperMutation.isSuccess && (
                                    <span style={{ alignSelf: 'center', fontSize: '0.8rem', color: 'rgb(var(--c-success-text))' }}>✓ Triggered</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Scraper Table Filter */}
                    <div style={{ marginBottom: '12px' }}>
                        <input
                            type="text"
                            placeholder="Filter scrapers..."
                            value={scraperTableFilter}
                            onChange={e => setScraperTableFilter(e.target.value)}
                            style={{
                                padding: '7px 12px',
                                fontSize: '13px',
                                border: '1px solid var(--border-color)',
                                borderRadius: '6px',
                                width: '260px',
                                background: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                            }}
                        />
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                                    <th 
                                        style={{ padding: '8px', cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('name')}
                                    >
                                        Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th style={{ padding: '8px' }}>URL</th>
                                    <th 
                                        style={{ padding: '8px', cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('lastChange')}
                                        title="Only updates when a new listing was actually found - not on every run, and not by any admin action (e.g. acknowledge, schedule change)"
                                    >
                                        Last New Listing {sortColumn === 'lastChange' && (sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th 
                                        style={{ padding: '8px', cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('results')}
                                    >
                                        Hits / New {sortColumn === 'results' && (sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th 
                                        style={{ padding: '8px', cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('status')}
                                    >
                                        Status {sortColumn === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th style={{ padding: '8px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {getSortedScrapers().filter(s => {
                                    if (!scraperTableFilter.trim()) return true;
                                    const q = scraperTableFilter.toLowerCase().trim();
                                    return formatScraperName(s.searchKey).toLowerCase().includes(q) ||
                                        s.searchKey.toLowerCase().includes(q);
                                }).map(s => (
                                    <React.Fragment key={s.searchKey}>
                                        <tr style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: s.isGroup ? 'var(--bg-secondary)' : 'transparent' }}>
                                             <td 
                                                data-label="Name"
                                                style={{ padding: '8px', fontSize: '12px', fontFamily: 'monospace' }}
                                            >
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {s.isGroup ? (
                                                            <button 
                                                                onClick={() => toggleScraperGroup(s.searchKey)}
                                                                className="scraper-group-toggle"
                                                                style={{ 
                                                                    border: 'none', 
                                                                    background: 'none', 
                                                                    cursor: 'pointer', 
                                                                    padding: 0,
                                                                    fontSize: 'inherit',
                                                                    fontFamily: 'inherit',
                                                                    fontWeight: 600,
                                                                    color: 'inherit',
                                                                    textDecoration: 'underline dotted',
                                                                    textAlign: 'left'
                                                                }}
                                                            >
                                                                {formatScraperName(s.searchKey)}
                                                            </button>
                                                        ) : (
                                                            s.publicSearchUrl ? (
                                                                <a href={s.publicSearchUrl} target="_blank" rel="noopener noreferrer" className="scraper-mobile-link" style={{ color: 'inherit', fontWeight: 400 }}>
                                                                    {formatScraperName(s.searchKey)}
                                                                </a>
                                                            ) : (
                                                                <span style={{ fontWeight: 400 }}>{formatScraperName(s.searchKey)}</span>
                                                            )
                                                         )}
                                                        <button
                                                            title={`Filter: ${s.searchKey.replace(/Scraper/, '')}`}
                                                            onClick={() => setScraperFilterInput(s.searchKey.replace(/Scraper/, ''))}
                                                            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: '10px', opacity: 0.4, lineHeight: 1 }}
                                                        >&#x25B6;</button>
                                                        {/* T4: VPS executor badge */}
                                                        {(s as any).executor === 'vps' && (
                                                            <span style={{ fontSize: '9px', fontWeight: 700, color: 'rgb(var(--c-on-brand-soft))', background: 'rgb(var(--c-brand-soft))', borderRadius: '3px', padding: '1px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                VPS
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                                                        {editingNote === s.searchKey ? (
                                                            <input 
                                                                type="text" 
                                                                value={noteValue} 
                                                                autoFocus
                                                                onChange={(e) => setNoteValue(e.target.value)}
                                                                onBlur={() => updateScraperNote(s.searchKey, noteValue)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') updateScraperNote(s.searchKey, noteValue);
                                                                    if (e.key === 'Escape') setEditingNote(null);
                                                                }}
                                                                style={{ 
                                                                    fontSize: '11px', 
                                                                    padding: '2px 6px', 
                                                                    border: '1px solid rgb(var(--c-brand))', 
                                                                    borderRadius: '4px', 
                                                                    width: '100%',
                                                                    minWidth: '150px'
                                                                }}
                                                            />
                                                        ) : (
                                                            <>
                                                                <span style={{ 
                                                                    color: 'rgb(var(--c-text-muted))', 
                                                                    fontSize: '11px', 
                                                                    fontStyle: 'italic', 
                                                                    maxWidth: '250px', 
                                                                    overflow: 'hidden', 
                                                                    textOverflow: 'ellipsis', 
                                                                    whiteSpace: 'nowrap' 
                                                                }}>
                                                                    {s.adminNote || 'Keine Notiz'}
                                                                </span>
                                                                <button 
                                                                    onClick={() => { setEditingNote(s.searchKey); setNoteValue(s.adminNote || ''); }}
                                                                    style={{ 
                                                                        border: 'none', 
                                                                        background: 'transparent', 
                                                                        cursor: 'pointer', 
                                                                        padding: 0, 
                                                                        display: 'flex', 
                                                                        opacity: 0.5,
                                                                        color: 'rgb(var(--c-text-muted))'
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                                                                >
                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                                    </svg>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td data-label="Link" className="hide-on-mobile" style={{ padding: '8px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {s.publicSearchUrl && (
                                                        <a 
                                                            href={s.publicSearchUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer" 
                                                            style={{ color: 'rgb(var(--c-brand))', textDecoration: 'none', fontWeight: 600 }}
                                                        >
                                                            {s.isGroup ? 'Link' : 'Link'}
                                                        </a>
                                                    )}
                                                    {(s.apiSearchUrl || s.lastScrapedUrl) && (s.apiSearchUrl || s.lastScrapedUrl) !== s.publicSearchUrl && (
                                                        <a 
                                                            href={s.apiSearchUrl || s.lastScrapedUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer" 
                                                            style={{ textDecoration: 'none', fontSize: '1.2em', filter: 'grayscale(1)', opacity: 0.4 }}
                                                            title="API URL"
                                                        >
                                                            ⚙️
                                                        </a>
                                                    )}
                                                    {!s.publicSearchUrl && !(s.apiSearchUrl || s.lastScrapedUrl) && '-'}
                                                </div>
                                            </td>
                                            <td data-label="Last New Listing" title="Timestamp of the last time a new listing was actually discovered - not the last run/check time, and unaffected by admin actions" style={{ padding: '8px' }}>{s.lastChange ? new Date(s.lastChange).toLocaleString() : '-'}</td>
                                            <td data-label="Results" style={{ padding: '8px' }}>
                                                {s.resultCount ?? '-'}
                                                {s.numOfNewEntries !== undefined && (
                                                    <span style={{ 
                                                        marginLeft: '6px', 
                                                        color: s.numOfNewEntries > 0 ? 'rgb(var(--c-success-text))' : 'rgb(var(--c-text-muted))',
                                                        fontWeight: s.numOfNewEntries > 0 ? 600 : 400
                                                    }}>
                                                        / {s.numOfNewEntries}
                                                    </span>
                                                )}
                                            </td>
                                            <td data-label="Status" style={{ padding: '8px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                                                    {/* Status Indicator */}
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        {(() => {
                                                            const errorCounter = s.errorCounter || 0;
                                                            const isDeactivated = s.deactivated;
                                                            const isFetching = s.tokenStatus === 'fetching';
                                                            // const lastErrorTs = s.lastErrorTimestamp || 0;
                                                            
                                                            // ... existing logic ...
                                                            let color = 'rgb(var(--c-success-text))'; // Healthy
                                                            let label = 'Healthy';

                                                            if (isDeactivated) {
                                                                color = 'rgb(var(--c-danger-text))';
                                                                label = 'Deactivated';
                                                            } else if (isFetching) {
                                                                color = 'rgb(var(--c-brand))';
                                                                label = `Fetching... (${Math.round((Date.now() - (s.lastTriggered || 0)) / 1000)}s)`;
                                                            } else if (errorCounter >= 10) {
                                                                color = 'rgb(var(--c-warning-text))';
                                                                label = `Critical (${errorCounter}/18)`;
                                                            } else if (errorCounter > 0) {
                                                                color = 'rgb(var(--c-warning-text))';
                                                                label = `Warning (${errorCounter}/10)`;
                                                            } else if (s.lastError && (s.lastRecoverTimestamp || 0) > (s.lastErrorTimestamp || 0)) {
                                                                label = 'Recovered';
                                                            }

                                                            return (
                                                                <>
                                                                    <div
                                                                        className={`scraper-status-dot${isFetching ? ' scraper-status-dot--fetching' : ''}`}
                                                                        style={{ backgroundColor: color }}
                                                                    ></div>
                                                                    <span className="scraper-status-label" style={{ color }}>
                                                                        {label}
                                                                    </span>
                                                                    {!isDeactivated && (
                                                                        <ScraperScheduleBadge
                                                                            searchKey={s.searchKey}
                                                                            schedule={s.schedule}
                                                                            isOpen={schedulePopover === s.searchKey}
                                                                            onToggle={() => setSchedulePopover(schedulePopover === s.searchKey ? null : s.searchKey)}
                                                                            onClose={() => setSchedulePopover(null)}
                                                                        />
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
    
                                                    {s.lastError && (
                                                        <button
                                                            onClick={() => setSelectedError(s)}
                                                            style={{
                                                                border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'flex', color: (s.errorCounter || 0) > 0 ? 'rgb(var(--c-warning-text))' : 'rgb(var(--c-text-muted))'
                                                            }}
                                                            title="Fehlerdetails anzeigen"
                                                        >
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                                                            </svg>
                                                        </button>
                                                    )}
                                                    </div>
                                            </td>
                                            <td data-label="Actions" style={{ padding: '8px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                                                    <button 
                                                        onClick={() => toggleScraper(s.searchKey, s.deactivated)}
                                                        title={s.deactivated ? 'Activate scraper' : 'Deactivate scraper'}
                                                        style={{ 
                                                            background: 'transparent', 
                                                            border: 'none', 
                                                            cursor: 'pointer',
                                                            padding: '4px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            opacity: 0.7,
                                                            transition: 'opacity 0.2s',
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                                                    >
                                                        {s.deactivated ? (
                                                            // Play icon for activate
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgb(var(--c-success-text))" strokeWidth="2">
                                                                <circle cx="12" cy="12" r="10"/>
                                                                <polygon points="10 8 16 12 10 16 10 8" fill="rgb(var(--c-success-text))"/>
                                                            </svg>
                                                        ) : (
                                                            // Pause icon for deactivate
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgb(var(--c-text-muted))" strokeWidth="2">
                                                                <circle cx="12" cy="12" r="10"/>
                                                                <line x1="10" y1="9" x2="10" y2="15" stroke="rgb(var(--c-text-muted))" strokeWidth="2"/>
                                                                <line x1="14" y1="9" x2="14" y2="15" stroke="rgb(var(--c-text-muted))" strokeWidth="2"/>
                                                            </svg>
                                                        )}
                                                    </button>
                                                    {s.deactivated && (
                                                        <button
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                const bucketKeys = s.buckets?.map(b => b.searchKey) ?? [];
                                                                const totalCount = 1 + bucketKeys.length;
                                                                const msg = bucketKeys.length > 0
                                                                    ? `Delete group "${s.searchKey}" and its ${bucketKeys.length} bucket record(s)?\nThis removes ${totalCount} DynamoDB entries permanently.`
                                                                    : `Delete scraper record "${s.searchKey}"?\nThis removes the DynamoDB entry permanently.`;
                                                                if (!window.confirm(msg)) return;
                                                                try {
                                                                    await deleteStaleScrapersMutation.mutateAsync([s.searchKey, ...bucketKeys]);
                                                                } catch (err: any) {
                                                                    alert(`Delete failed: ${err.message}`);
                                                                }
                                                            }}
                                                            title="Delete this deactivated record permanently"
                                                            style={{
                                                                background: 'transparent',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                padding: '4px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                opacity: 0.6,
                                                                color: 'rgb(var(--c-danger-text))',
                                                                transition: 'opacity 0.2s',
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                                            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="3 6 5 6 21 6"/>
                                                                <path d="M19 6l-1 14H6L5 6"/>
                                                                <path d="M10 11v6M14 11v6"/>
                                                                <path d="M9 6V4h6v2"/>
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
    
                                        {/* Bucket Rows */}
                                        {s.isGroup && expandedScrapers.has(s.searchKey) && s.buckets?.map(b => (
                                            <tr 
                                                key={b.searchKey} 
                                                style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-main)' }}
                                            >
                                                <td style={{ 
                                                    padding: '8px 8px 8px 32px', 
                                                    fontSize: '11px', 
                                                    fontFamily: 'monospace', 
                                                    color: 'var(--text-muted)'
                                                }}>
                                                    {formatScraperName(b.searchKey)}
                                                </td>
                                                <td style={{ 
                                                    padding: '8px'
                                                }}>
                                                    {b.publicSearchUrl && (
                                                        <a 
                                                            href={b.publicSearchUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer" 
                                                            style={{ color: 'rgb(var(--c-brand))', textDecoration: 'none', fontSize: '11px' }}
                                                        >
                                                            District Link
                                                        </a>
                                                    )}
                                                    {(b.apiSearchUrl || b.lastScrapedUrl) && (b.apiSearchUrl || b.lastScrapedUrl) !== b.publicSearchUrl && (
                                                        <a
                                                            href={b.apiSearchUrl || b.lastScrapedUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="API URL"
                                                            style={{ marginLeft: '8px', textDecoration: 'none', fontSize: '11px', filter: 'grayscale(1)', opacity: 0.4 }}
                                                        >
                                                            ⚙️
                                                        </a>
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px', fontSize: '11px', color: 'var(--text-muted)' }} title="Last new listing found (not last run)">{b.lastChange ? new Date(b.lastChange).toLocaleString() : '-'}</td>
                                                <td style={{ padding: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
                                                    {b.resultCount ?? '-'}
                                                    {b.numOfNewEntries !== undefined && (
                                                        <span style={{ 
                                                            marginLeft: '4px', 
                                                            color: b.numOfNewEntries > 0 ? 'rgb(var(--c-success-text))' : 'rgb(var(--c-text-muted))',
                                                            fontWeight: b.numOfNewEntries > 0 ? 600 : 400
                                                        }}>
                                                            / {b.numOfNewEntries}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px', fontSize: '11px', color: 'var(--text-muted)' }} colSpan={2}>
                                                    <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Managed by Group</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* System Actions Section */}
                <div id="system" className="section" style={{ marginBottom: '40px' }}>
                    <div className="section-header">
                        <h2>Globale System-Aktionen</h2>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <ScheduleProfilesPanel />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                        {/* Stats Card */}
                        <div className="admin-system-card">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <h4 style={{ margin: 0, color: 'var(--text-main)' }}>System-Status</h4>
                                <RefreshButton onClick={handleRefresh} spinning={isRefreshing} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Aktiv überwachte PLZ</span>
                                    <span style={{ fontWeight: 600, color: 'var(--text-main)' }}>{systemStats?.zipCount ?? '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Gesamtzahl Subscriptions</span>
                                    <span style={{ fontWeight: 600, color: 'var(--text-main)' }}>{systemStats?.totalSubscriptions ?? '-'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Maintenance Mode</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ 
                                            fontWeight: 600, 
                                            color: isMaintenance ? 'rgb(var(--c-danger-text))' : 'rgb(var(--c-success-text))',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}>
                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'currentColor' }}></div>
                                            {isMaintenance ? `${systemStatsData?.maintenanceSeverity ?? 'Aktiv'}` : 'Inaktiv'}
                                        </span>
                                        <button
                                            onClick={() => setShowMaintenanceForm(v => !v)}
                                            className="admin-btn-utility"
                                        >
                                            ✏️ {isMaintenance ? 'Bearbeiten / Deaktivieren' : 'Aktivieren'}
                                        </button>
                                    </div>
                                </div>
                                {showMaintenanceForm && (
                                    <div style={{ marginTop: '12px', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                                        <div style={{ marginBottom: '10px' }}>
                                            <label className="admin-label">Schweregrad</label>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                {(['INFO', 'WARNING', 'MAINTENANCE', 'OUTAGE'] as const).map(sev => (
                                                    <button
                                                        key={sev}
                                                        onClick={() => setMaintenanceSeverity(sev)}
                                                        className={`admin-severity-btn ${sev.toLowerCase()} ${maintenanceSeverity === sev ? 'active' : ''}`}
                                                    >
                                                        {sev === 'INFO' ? 'ℹ️ INFO' : sev === 'WARNING' ? '⚠️ WARNING' : sev === 'MAINTENANCE' ? '🔧 MAINTENANCE' : '🔴 OUTAGE'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: '10px' }}>
                                            <label className="admin-label">Nachricht</label>
                                            <input
                                                type="text"
                                                value={maintenanceText}
                                                onChange={e => setMaintenanceText(e.target.value)}
                                                placeholder="Nachricht für Benutzer..."
                                                className="admin-input"
                                            />
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                disabled={isTogglingMaintenance}
                                                onClick={async () => {
                                                    setIsTogglingMaintenance(true);
                                                    try {
                                                        await setMaintenanceModeMutation.mutateAsync({ active: true, severity: maintenanceSeverity, text: maintenanceText });
                                                        setShowMaintenanceForm(false);
                                                    } finally { setIsTogglingMaintenance(false); }
                                                }}
                                                style={{ padding: '6px 14px', background: 'rgb(var(--c-warning-text))', color: 'rgb(var(--c-surface-card))', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                                            >
                                                {isTogglingMaintenance ? '...' : '✅ Aktivieren'}
                                            </button>
                                            {isMaintenance && (
                                                <button
                                                    disabled={isTogglingMaintenance}
                                                    onClick={async () => {
                                                        setIsTogglingMaintenance(true);
                                                        try {
                                                            await setMaintenanceModeMutation.mutateAsync({ active: false });
                                                            setShowMaintenanceForm(false);
                                                        } finally { setIsTogglingMaintenance(false); }
                                                    }}
                                                    style={{ padding: '6px 14px', background: 'rgb(var(--c-success-text))', color: 'rgb(var(--c-surface-card))', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                                                >
                                                    {isTogglingMaintenance ? '...' : '🔓 Deaktivieren'}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setShowMaintenanceForm(false)}
                                                className="admin-btn-utility"
                                            >
                                                Abbrechen
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Rebuild Card */}
                        <div className="admin-system-card">
                            <h4 style={{ margin: '0 0 16px 0', color: 'var(--text-main)' }}>Wartungs-Tools</h4>
                            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                                Nutzen Sie diese Tools, um System-Aggregate neu zu berechnen, falls Daten inkonsistent wirken.
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                <button 
                                    className={`btn admin-rebuild-btn-blue ${isRebuilding === 'metadata' ? 'disabled' : ''}`}
                                    disabled={!!isRebuilding}
                                    onClick={() => handleRebuild('metadata')}
                                    style={{ 
                                        fontSize: '13px',
                                        padding: '10px 16px'
                                    }}
                                >
                                    {isRebuilding === 'metadata' ? '🔄 Metadata...' : 'SubscriptionsAggregate Neu Aufbauen'}
                                </button>
                            </div>
                        </div>

                        {/* Stale Cleanup Card */}
                        <div className="admin-system-card">
                            <h4 style={{ margin: '0 0 16px 0', color: 'var(--text-main)' }}>Stale Entries Cleanup</h4>
                            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                                Verwaiste DynamoDB-Einträge von Partitioned Scrapers erkennen und entfernen.
                            </p>
                            {!showStalePanel ? (
                                <button
                                    onClick={() => { setShowStalePanel(true); setSelectedStaleKeys(new Set()); }}
                                    className="btn admin-rebuild-btn-blue"
                                    style={{ fontSize: '13px', padding: '10px 16px' }}
                                >
                                    🔍 Stale Einträge erkennen
                                </button>
                            ) : isLoadingStale ? (
                                <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Analysiere...</div>
                            ) : staleData && staleData.staleCount === 0 ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: 'rgb(var(--c-success-text))', fontWeight: 600, fontSize: '13px' }}>✅ Keine stale Einträge gefunden</span>
                                    <button onClick={() => setShowStalePanel(false)} className="admin-btn-utility">Schließen</button>
                                </div>
                            ) : staleData ? (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                                            {staleData.staleCount} stale von {staleData.totalHeadRecords} Einträgen
                                        </span>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button
                                                onClick={() => {
                                                    if (selectedStaleKeys.size === staleData.staleRecords.length) {
                                                        setSelectedStaleKeys(new Set());
                                                    } else {
                                                        setSelectedStaleKeys(new Set(staleData.staleRecords.map(r => r.searchKey)));
                                                    }
                                                }}
                                                className="admin-btn-utility"
                                                style={{ fontSize: '11px' }}
                                            >
                                                {selectedStaleKeys.size === staleData.staleRecords.length ? 'Keine' : 'Alle'} auswählen
                                            </button>
                                            <button onClick={() => refetchStale()} className="admin-btn-utility" style={{ fontSize: '11px' }}>
                                                ↻ Neu laden
                                            </button>
                                        </div>
                                    </div>
                                    <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                                        <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                                                    <th style={{ padding: '6px 8px', width: '30px' }}></th>
                                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Key</th>
                                                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Grund</th>
                                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Alter</th>
                                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>IDs</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {staleData.staleRecords.map((record: StaleRecord) => {
                                                    const ageDays = record.lastChange ? Math.round((Date.now() - record.lastChange) / (1000 * 60 * 60 * 24)) : -1;
                                                    const reasonLabel = record.reason === 'orphaned-bucket' ? '🔗 Verwaist'
                                                        : record.reason === 'stale-group' ? '📦 Stale Gruppe'
                                                        : '🪣 Stale Bucket';
                                                    return (
                                                        <tr key={record.searchKey} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                            <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedStaleKeys.has(record.searchKey)}
                                                                    onChange={(e) => {
                                                                        const next = new Set(selectedStaleKeys);
                                                                        if (e.target.checked) next.add(record.searchKey);
                                                                        else next.delete(record.searchKey);
                                                                        setSelectedStaleKeys(next);
                                                                    }}
                                                                />
                                                            </td>
                                                            <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: '11px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                                title={record.searchKey}
                                                            >
                                                                {record.searchKey.replace(/Scraper/, '')}
                                                            </td>
                                                            <td style={{ padding: '4px 8px' }}>{reasonLabel}</td>
                                                            <td style={{ padding: '4px 8px', textAlign: 'right', color: ageDays > 30 ? 'rgb(var(--c-danger-text))' : 'var(--text-muted)' }}>
                                                                {ageDays >= 0 ? `${ageDays}d` : '-'}
                                                            </td>
                                                            <td style={{ padding: '4px 8px', textAlign: 'right' }}>{record.listingCount}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                        <button
                                            disabled={selectedStaleKeys.size === 0 || isDeletingStale}
                                            onClick={async () => {
                                                if (!window.confirm(`${selectedStaleKeys.size} Einträge endgültig löschen?`)) return;
                                                setIsDeletingStale(true);
                                                try {
                                                    const result = await deleteStaleScrapersMutation.mutateAsync(Array.from(selectedStaleKeys));
                                                    alert(`${result.deleted.length} gelöscht${result.failed.length > 0 ? `, ${result.failed.length} fehlgeschlagen` : ''}`);
                                                    setSelectedStaleKeys(new Set());
                                                    refetchStale();
                                                } catch (err: any) {
                                                    alert(`Fehler: ${err.message}`);
                                                } finally {
                                                    setIsDeletingStale(false);
                                                }
                                            }}
                                            style={{
                                                padding: '6px 14px',
                                                background: selectedStaleKeys.size > 0 ? 'rgb(var(--c-danger-text))' : 'rgb(var(--c-text-muted))',
                                                color: 'rgb(var(--c-surface-card))',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                cursor: selectedStaleKeys.size > 0 ? 'pointer' : 'not-allowed',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {isDeletingStale ? '...' : `🗑️ ${selectedStaleKeys.size} Einträge löschen`}
                                        </button>
                                        <button onClick={() => setShowStalePanel(false)} className="admin-btn-utility">
                                            Schließen
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                    </div>
                </div>

                {/* Users Section */}
                <div id="users" className="section">
                    <div className="section-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <h2>Benutzerverwaltung</h2>
                            <RefreshButton onClick={async () => {
                                setIsRefreshingUsers(true);
                                const minDelay = new Promise(resolve => setTimeout(resolve, 500));
                                try { await Promise.all([refetchUsers(), minDelay]); } finally { setIsRefreshingUsers(false); }
                            }} spinning={isRefreshingUsers} />
                        </div>
                    </div>
                    <AdminUserTable
                      users={users}
                      onOpenDetail={(u) => setSelectedUser(u)}
                      onImpersonate={async (userId) => {
                        try {
                          const { token } = await impersonateUser(userId);
                          const link = `${window.location.origin}/impersonate?token=${encodeURIComponent(token)}`;
                          setImpersonationLink(link);
                        } catch (err) {
                          console.error('Impersonation failed:', err);
                          alert('Impersonation fehlgeschlagen');
                        }
                      }}
                      searchQuery={userSearchQuery}
                      onSearchChange={setUserSearchQuery}
                    />
                </div>
            </>
        )}

        {/* User Detail Dialog */}
        {selectedUser && (
            <AdminUserDialog
              user={selectedUser}
              onClose={() => setSelectedUser(null)}
              isBlocked={!!selectedSubscription}
              onUpdate={updateUser}
              onResetPassword={(u) => { handleResetPassword(u); }}
              onDeleteUser={(u) => { handleDeleteUser(u); setSelectedUser(null); }}
              onTestPush={handleTestPush}
              onTestEmail={handleTestEmail}
              onTestTelegram={handleTestTelegram}
              onEditSubscription={setSelectedSubscription}
              onResetEmailVerification={async (userId) => {
                await resetEmailVerification(userId);
                queryClient.setQueryData<UserData[]>(adminKeys.users(), (old) =>
                  old?.map((u) =>
                    u.userId === userId
                      ? { ...u, emailVerified: false, verificationToken: null, verificationTokenExpires: null }
                      : u
                  )
                );
              }}
              isResetting={isResetting}
              isDeleting={isDeleting}
              isSendingPush={sendPushTestMutation.isPending}
              isSendingEmail={sendTestEmailMutation.isPending}
              isSendingTelegram={sendTestTelegramMutation.isPending}
            />
        )}

        {/* Password Reset Modal */}
        {resetResult && (
            <div className="admin-modal-overlay">
                <div className="admin-modal-content" style={{ maxWidth: '400px' }}>
                    <h3 style={{ marginTop: 0, color: 'var(--text-main)' }}>Neues Passwort generiert</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Password für: <strong>{resetResult.email}</strong></p>
                    <div style={{ 
                        backgroundColor: 'var(--bg-main)', padding: '12px', borderRadius: '4px', fontSize: '18px', 
                        fontFamily: 'monospace', textAlign: 'center', margin: '16px 0', border: '1px dashed var(--border-color)',
                        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', color: 'var(--text-main)'
                    }}>
                        {resetResult.newPassword}
                        <button onClick={() => copyToClipboard(resetResult.newPassword!)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>📋</button>
                    </div>
                    <button 
                        onClick={() => setResetResult(null)} 
                        className="btn" 
                        style={{ width: '100%', marginTop: '8px' }}
                    >
                        Schließen
                    </button>
                </div>
            </div>
        )}

        {/* Error Details Modal */}
        {selectedError && (
            <div className="admin-modal-overlay">
                <div className="admin-modal-content" style={{ maxWidth: '600px', maxHeight: '85vh', overflowY: 'auto', overscrollBehavior: 'contain' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '20px', color: 'var(--text-main)' }}>Scraper Error Details</h3>
                            <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '14px', fontFamily: 'monospace' }}>
                                {selectedError.searchKey}
                            </p>
                        </div>
                        <button 
                            onClick={() => setSelectedError(null)}
                            style={{ border: 'none', background: 'var(--bg-secondary)', padding: '8px', borderRadius: '50%', cursor: 'pointer', display: 'flex' }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2.5">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                        <div style={{ backgroundColor: 'var(--bg-main)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                            <div style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Error Type</div>
                            <div style={{ color: 'var(--accent-red)', fontWeight: 600 }}>{selectedError.lastErrorType || 'UNKNOWN'}</div>
                        </div>
                        <div style={{ backgroundColor: 'var(--bg-main)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                            <div style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Consecutive Errors</div>
                            <div style={{ color: 'var(--accent-orange)', fontWeight: 600 }}>{selectedError.errorCounter || 0}</div>
                        </div>
                        <div style={{ backgroundColor: 'var(--bg-main)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                            <div style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Last Occurred</div>
                            <div style={{ color: 'var(--text-main)' }}>{selectedError.lastErrorTimestamp ? new Date(selectedError.lastErrorTimestamp).toLocaleString() : 'N/A'}</div>
                        </div>
                        <div style={{ backgroundColor: 'var(--bg-main)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                            <div style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Retry-After</div>
                            <div style={{ color: 'var(--text-main)', fontWeight: 600 }}>{selectedError.retryAfter || 'N/A'}</div>
                        </div>
                        <div style={{ backgroundColor: 'var(--bg-main)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                            <div style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Source URL</div>
                            <div style={{ color: 'var(--accent-blue)', wordBreak: 'break-all' }}>
                                {selectedError.lastScrapedUrl ? <a href={selectedError.lastScrapedUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>Link 🔗</a> : 'N/A'}
                            </div>
                        </div>
                        <div style={{ backgroundColor: 'var(--bg-main)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                            <div style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '4px' }}>Last Recovered</div>
                            <div style={{ color: 'var(--text-main)', fontWeight: 600 }}>
                                {selectedError.lastRecoverTimestamp ? new Date(selectedError.lastRecoverTimestamp).toLocaleString() : 'Never'}
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '8px' }}>Full Error Message</div>
                        <div style={{ 
                            backgroundColor: 'var(--bg-main)', color: 'var(--text-main)', padding: '16px', borderRadius: '8px', 
                            fontSize: '13px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', maxHeight: '300px', overflowY: 'auto',
                            border: '1px solid var(--border-color)', lineHeight: '1.5'
                        }}>
                            {selectedError.lastError || 'No error message available.'}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        {!!((selectedError.errorCounter || 0) > 0 || selectedError.lastError || selectedError.deactivated) && (
                            <button 
                                onClick={handleAcknowledgeError}
                                disabled={isAcknowledging}
                                className="btn" 
                                style={{ 
                                    flex: 1,
                                    backgroundColor: isAcknowledging ? 'var(--btn-disabled-bg)' : 'var(--accent-blue)', 
                                    border: 'none', 
                                    color: 'rgb(var(--c-on-brand))', 
                                    padding: '12px', 
                                    borderRadius: '8px', 
                                    fontWeight: 600,
                                    cursor: isAcknowledging ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {isAcknowledging ? 'Acknowledging...' : 'Acknowledge Error ✓'}
                            </button>
                        )}
                        <button 
                            onClick={() => setSelectedError(null)} 
                            className="btn" 
                            style={{ 
                                flex: ((selectedError.errorCounter || 0) > 0 || selectedError.lastError || selectedError.deactivated) ? 1 : 'auto',
                                width: ((selectedError.errorCounter || 0) > 0 || selectedError.lastError || selectedError.deactivated) ? 'auto' : '100%',
                                backgroundColor: 'var(--accent-blue)', 
                                border: 'none', 
                                color: 'rgb(var(--c-on-brand))', 
                                padding: '12px', 
                                borderRadius: '8px', 
                                fontWeight: 600 
                            }}
                        >Schließen
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* Admin Subscription Editor Modal */}
        {selectedSubscription && (
          <AdminSubscriptionModal
            subscription={selectedSubscription}
            onClose={() => setSelectedSubscription(null)}
            onSaved={() => {}}
          />
        )}

        {/* Impersonation link modal */}
        {impersonationLink && (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgb(var(--c-scrim) / 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
               onClick={() => setImpersonationLink(null)}>
            <div style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '24px', maxWidth: '480px', width: '90%', boxShadow: '0 20px 60px rgb(0 0 0 / 0.4)' }}
                 onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '15px' }}>🕵️ Impersonation starten</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 16px 0', lineHeight: 1.5 }}>
                Öffne diesen Link in einem <strong>privaten Fenster</strong> (InPrivate / Incognito).
                So bleiben deine Admin-Cookies von der Impersonation getrennt.
              </p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', background: 'var(--bg-main)', border: '1px solid var(--border-color)', borderRadius: '4px', padding: '8px 10px', marginBottom: '16px' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-muted)', wordBreak: 'break-all' as const, flex: 1 }}>
                  {impersonationLink}
                </span>
                <button
                  onClick={() => { navigator.clipboard.writeText(impersonationLink); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', flexShrink: 0, padding: '0 4px' }}
                  title="Kopieren"
                >📋</button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button className="admin-btn-secondary" onClick={() => setImpersonationLink(null)}>Schließen</button>
                <a
                  className="admin-btn-primary"
                  href={impersonationLink}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'none' }}
                >Trotzdem öffnen (selbes Profil)</a>
              </div>
            </div>
          </div>
        )}

        {/* Notification Modal */}
        {showNotificationModal && (
          <NotificationModal 
            onClose={() => setShowNotificationModal(false)}
            onSend={async (data) => {
                setNotificationProgress('sending');
                try {
                    await sendNotificationMutation.mutateAsync(data);
                    setNotificationProgress('success');
                    setTimeout(() => setShowNotificationModal(false), 2000);
                } catch (err: any) {
                    alert(`Fehler: ${err.response?.data?.error || err.message}`);
                    setNotificationProgress('idle');
                }
            }}
            progress={notificationProgress}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPage;
